import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2, Edit2 } from 'lucide-react'
import { toast } from 'sonner'
import { crmApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { LeadSource, LeadStatus, LeadTag, CreateLeadSourceRequest, CreateLeadStatusRequest, CreateLeadTagRequest } from '@/types'

const DEFAULT_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16']

export default function LeadSettingsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  // Sources state
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false)
  const [editingSource, setEditingSource] = useState<LeadSource | null>(null)
  const [sourceForm, setSourceForm] = useState<CreateLeadSourceRequest>({ source_name: '', source_description: '' })

  // Statuses state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const [editingStatus, setEditingStatus] = useState<LeadStatus | null>(null)
  const [statusForm, setStatusForm] = useState<CreateLeadStatusRequest>({ status_name: '', status_color: '#3B82F6' })

  // Tags state
  const [tagDialogOpen, setTagDialogOpen] = useState(false)
  const [editingTag, setEditingTag] = useState<LeadTag | null>(null)
  const [tagForm, setTagForm] = useState<CreateLeadTagRequest>({ tag_name: '', tag_color: '#3B82F6' })

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{ type: 'source' | 'status' | 'tag'; id: string; name: string } | null>(null)

  // Queries
  const { data: sources = [] } = useQuery({
    queryKey: ['lead-sources-all'],
    queryFn: () => crmApi.getLeadSources(false),
  })

  const { data: statuses = [] } = useQuery({
    queryKey: ['lead-statuses-all'],
    queryFn: () => crmApi.getLeadStatuses(false),
  })

  const { data: tags = [] } = useQuery({
    queryKey: ['lead-tags-all'],
    queryFn: () => crmApi.getLeadTags(false),
  })

  // Source mutations
  const createSourceMutation = useMutation({
    mutationFn: (data: CreateLeadSourceRequest) => crmApi.createLeadSource(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-sources-all'] })
      queryClient.invalidateQueries({ queryKey: ['lead-sources'] })
      toast.success(t('messages.createSuccess'))
      setSourceDialogOpen(false)
      setSourceForm({ source_name: '', source_description: '' })
    },
    onError: () => toast.error(t('messages.createError')),
  })

  const updateSourceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateLeadSourceRequest }) => crmApi.updateLeadSource(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-sources-all'] })
      queryClient.invalidateQueries({ queryKey: ['lead-sources'] })
      toast.success(t('messages.updateSuccess'))
      setSourceDialogOpen(false)
      setEditingSource(null)
      setSourceForm({ source_name: '', source_description: '' })
    },
    onError: () => toast.error(t('messages.updateError')),
  })

  const deleteSourceMutation = useMutation({
    mutationFn: (id: string) => crmApi.deleteLeadSource(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-sources-all'] })
      queryClient.invalidateQueries({ queryKey: ['lead-sources'] })
      toast.success(t('messages.deleteSuccess'))
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    },
    onError: () => toast.error(t('messages.deleteError')),
  })

  // Status mutations
  const createStatusMutation = useMutation({
    mutationFn: (data: CreateLeadStatusRequest) => crmApi.createLeadStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-statuses-all'] })
      queryClient.invalidateQueries({ queryKey: ['lead-statuses'] })
      toast.success(t('messages.createSuccess'))
      setStatusDialogOpen(false)
      setStatusForm({ status_name: '', status_color: '#3B82F6' })
    },
    onError: () => toast.error(t('messages.createError')),
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateLeadStatusRequest }) => crmApi.updateLeadStatus(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-statuses-all'] })
      queryClient.invalidateQueries({ queryKey: ['lead-statuses'] })
      toast.success(t('messages.updateSuccess'))
      setStatusDialogOpen(false)
      setEditingStatus(null)
      setStatusForm({ status_name: '', status_color: '#3B82F6' })
    },
    onError: () => toast.error(t('messages.updateError')),
  })

  const deleteStatusMutation = useMutation({
    mutationFn: (id: string) => crmApi.deleteLeadStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-statuses-all'] })
      queryClient.invalidateQueries({ queryKey: ['lead-statuses'] })
      toast.success(t('messages.deleteSuccess'))
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    },
    onError: () => toast.error(t('messages.deleteError')),
  })

  // Tag mutations
  const createTagMutation = useMutation({
    mutationFn: (data: CreateLeadTagRequest) => crmApi.createLeadTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-tags-all'] })
      queryClient.invalidateQueries({ queryKey: ['lead-tags'] })
      toast.success(t('messages.createSuccess'))
      setTagDialogOpen(false)
      setTagForm({ tag_name: '', tag_color: '#3B82F6' })
    },
    onError: () => toast.error(t('messages.createError')),
  })

  const updateTagMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateLeadTagRequest }) => crmApi.updateLeadTag(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-tags-all'] })
      queryClient.invalidateQueries({ queryKey: ['lead-tags'] })
      toast.success(t('messages.updateSuccess'))
      setTagDialogOpen(false)
      setEditingTag(null)
      setTagForm({ tag_name: '', tag_color: '#3B82F6' })
    },
    onError: () => toast.error(t('messages.updateError')),
  })

  const deleteTagMutation = useMutation({
    mutationFn: (id: string) => crmApi.deleteLeadTag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-tags-all'] })
      queryClient.invalidateQueries({ queryKey: ['lead-tags'] })
      toast.success(t('messages.deleteSuccess'))
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    },
    onError: () => toast.error(t('messages.deleteError')),
  })

  // Handlers
  const handleEditSource = (source: LeadSource) => {
    setEditingSource(source)
    setSourceForm({ source_name: source.source_name, source_description: source.source_description || '' })
    setSourceDialogOpen(true)
  }

  const handleEditStatus = (status: LeadStatus) => {
    setEditingStatus(status)
    setStatusForm({ status_name: status.status_name, status_color: status.status_color })
    setStatusDialogOpen(true)
  }

  const handleEditTag = (tag: LeadTag) => {
    setEditingTag(tag)
    setTagForm({ tag_name: tag.tag_name, tag_color: tag.tag_color })
    setTagDialogOpen(true)
  }

  const handleDelete = (type: 'source' | 'status' | 'tag', id: string, name: string) => {
    setItemToDelete({ type, id, name })
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (!itemToDelete) return
    if (itemToDelete.type === 'source') deleteSourceMutation.mutate(itemToDelete.id)
    else if (itemToDelete.type === 'status') deleteStatusMutation.mutate(itemToDelete.id)
    else deleteTagMutation.mutate(itemToDelete.id)
  }

  const handleSourceSubmit = () => {
    if (!sourceForm.source_name.trim()) {
      toast.error(t('crm.settings.nameRequired'))
      return
    }
    if (editingSource) {
      updateSourceMutation.mutate({ id: editingSource.source_id, data: sourceForm })
    } else {
      createSourceMutation.mutate(sourceForm)
    }
  }

  const handleStatusSubmit = () => {
    if (!statusForm.status_name.trim()) {
      toast.error(t('crm.settings.nameRequired'))
      return
    }
    if (editingStatus) {
      updateStatusMutation.mutate({ id: editingStatus.status_id, data: statusForm })
    } else {
      createStatusMutation.mutate(statusForm)
    }
  }

  const handleTagSubmit = () => {
    if (!tagForm.tag_name.trim()) {
      toast.error(t('crm.settings.nameRequired'))
      return
    }
    if (editingTag) {
      updateTagMutation.mutate({ id: editingTag.tag_id, data: tagForm })
    } else {
      createTagMutation.mutate(tagForm)
    }
  }

  return (
    <div className="space-y-4">
      <div className="-mt-2">
        <h1 className="text-2xl font-bold">{t('crm.settings.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('crm.settings.subtitle')}</p>
      </div>

      <Tabs defaultValue="sources" className="space-y-4">
        <TabsList>
          <TabsTrigger value="sources">{t('crm.settings.sources')}</TabsTrigger>
          <TabsTrigger value="statuses">{t('crm.settings.statuses')}</TabsTrigger>
          <TabsTrigger value="tags">{t('crm.settings.tags')}</TabsTrigger>
        </TabsList>

        {/* Sources Tab */}
        <TabsContent value="sources">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('crm.settings.leadSources')}</CardTitle>
                <CardDescription>{t('crm.settings.sourcesDescription')}</CardDescription>
              </div>
              <Button onClick={() => { setEditingSource(null); setSourceForm({ source_name: '', source_description: '' }); setSourceDialogOpen(true) }}>
                <Plus className="mr-2 h-4 w-4" />
                {t('crm.settings.addSource')}
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.name')}</TableHead>
                    <TableHead>{t('common.description')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="w-[100px]">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sources.map((source: LeadSource) => (
                    <TableRow key={source.source_id}>
                      <TableCell className="font-medium">{source.source_name}</TableCell>
                      <TableCell>{source.source_description || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={source.is_active ? 'default' : 'secondary'}>
                          {source.is_active ? t('common.active') : t('common.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEditSource(source)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete('source', source.source_id, source.source_name)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {sources.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        {t('crm.settings.noSources')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Statuses Tab */}
        <TabsContent value="statuses">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('crm.settings.leadStatuses')}</CardTitle>
                <CardDescription>{t('crm.settings.statusesDescription')}</CardDescription>
              </div>
              <Button onClick={() => { setEditingStatus(null); setStatusForm({ status_name: '', status_color: '#3B82F6' }); setStatusDialogOpen(true) }}>
                <Plus className="mr-2 h-4 w-4" />
                {t('crm.settings.addStatus')}
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.name')}</TableHead>
                    <TableHead>{t('common.color')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="w-[100px]">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statuses.map((status: LeadStatus) => (
                    <TableRow key={status.status_id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: status.status_color }} />
                          {status.status_name}
                          {status.is_default && <Badge variant="outline" className="ml-2">{t('common.default')}</Badge>}
                          {status.is_converted && <Badge variant="secondary" className="ml-2">{t('crm.settings.converted')}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>{status.status_color}</TableCell>
                      <TableCell>
                        <Badge variant={status.is_active ? 'default' : 'secondary'}>
                          {status.is_active ? t('common.active') : t('common.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEditStatus(status)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete('status', status.status_id, status.status_name)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {statuses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        {t('crm.settings.noStatuses')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('crm.settings.leadTags')}</CardTitle>
                <CardDescription>{t('crm.settings.tagsDescription')}</CardDescription>
              </div>
              <Button onClick={() => { setEditingTag(null); setTagForm({ tag_name: '', tag_color: '#3B82F6' }); setTagDialogOpen(true) }}>
                <Plus className="mr-2 h-4 w-4" />
                {t('crm.settings.addTag')}
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.name')}</TableHead>
                    <TableHead>{t('common.color')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="w-[100px]">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tags.map((tag: LeadTag) => (
                    <TableRow key={tag.tag_id}>
                      <TableCell className="font-medium">
                        <Badge style={{ backgroundColor: tag.tag_color, color: '#fff' }}>
                          {tag.tag_name}
                        </Badge>
                      </TableCell>
                      <TableCell>{tag.tag_color}</TableCell>
                      <TableCell>
                        <Badge variant={tag.is_active ? 'default' : 'secondary'}>
                          {tag.is_active ? t('common.active') : t('common.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEditTag(tag)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete('tag', tag.tag_id, tag.tag_name)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {tags.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        {t('crm.settings.noTags')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Source Dialog */}
      <Dialog open={sourceDialogOpen} onOpenChange={setSourceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSource ? t('crm.settings.editSource') : t('crm.settings.addSource')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('common.name')} *</Label>
              <Input value={sourceForm.source_name} onChange={(e) => setSourceForm({ ...sourceForm, source_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>{t('common.description')}</Label>
              <Input value={sourceForm.source_description} onChange={(e) => setSourceForm({ ...sourceForm, source_description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSourceDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSourceSubmit} disabled={createSourceMutation.isPending || updateSourceMutation.isPending}>
              {editingSource ? t('common.update') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingStatus ? t('crm.settings.editStatus') : t('crm.settings.addStatus')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('common.name')} *</Label>
              <Input value={statusForm.status_name} onChange={(e) => setStatusForm({ ...statusForm, status_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>{t('common.color')}</Label>
              <div className="flex items-center gap-2">
                <Input type="color" value={statusForm.status_color} onChange={(e) => setStatusForm({ ...statusForm, status_color: e.target.value })} className="w-16 h-10 p-1" />
                <div className="flex gap-1">
                  {DEFAULT_COLORS.map((color) => (
                    <button key={color} type="button" className="w-6 h-6 rounded-full border-2" style={{ backgroundColor: color, borderColor: statusForm.status_color === color ? '#000' : 'transparent' }} onClick={() => setStatusForm({ ...statusForm, status_color: color })} />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleStatusSubmit} disabled={createStatusMutation.isPending || updateStatusMutation.isPending}>
              {editingStatus ? t('common.update') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Dialog */}
      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTag ? t('crm.settings.editTag') : t('crm.settings.addTag')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('common.name')} *</Label>
              <Input value={tagForm.tag_name} onChange={(e) => setTagForm({ ...tagForm, tag_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>{t('common.color')}</Label>
              <div className="flex items-center gap-2">
                <Input type="color" value={tagForm.tag_color} onChange={(e) => setTagForm({ ...tagForm, tag_color: e.target.value })} className="w-16 h-10 p-1" />
                <div className="flex gap-1">
                  {DEFAULT_COLORS.map((color) => (
                    <button key={color} type="button" className="w-6 h-6 rounded-full border-2" style={{ backgroundColor: color, borderColor: tagForm.tag_color === color ? '#000' : 'transparent' }} onClick={() => setTagForm({ ...tagForm, tag_color: color })} />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleTagSubmit} disabled={createTagMutation.isPending || updateTagMutation.isPending}>
              {editingTag ? t('common.update') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.delete')}</DialogTitle>
            <DialogDescription>
              {t('messages.confirmDelete')} <strong>{itemToDelete?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteSourceMutation.isPending || deleteStatusMutation.isPending || deleteTagMutation.isPending}>
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

